/**
 * El diario de a bordo: todo lo que la aplicación se queja por consola, guardado
 * por si luego hace falta.
 *
 * Existe por una razón concreta: cuando algo se tuerce en una clase, lo que
 * cuenta qué pasó está en la consola del navegador del profesor — y para
 * entonces ya ha recargado, o no sabe abrirla, o el error ocurrió veinte
 * minutos antes de que nadie se diera cuenta. Aquí queda, y el modo depuración
 * (cinco clics en el copyright de Ajustes) lo enseña y permite copiarlo entero.
 *
 * Se engancha al IMPORTARSE, desde main.ts, no cuando se activa el modo
 * depuración: el error que importa casi siempre es anterior a la sospecha.
 *
 * No reemplaza la consola, la escucha: cada mensaje sigue saliendo por donde
 * salía. Y nunca sale del navegador — no se envía a ningún sitio.
 */

export interface DebugEntry {
  /** Milisegundos desde que arrancó la página: la hora del reloj no dice nada
   *  cuando lo que importa es «esto fue justo antes de aquello». */
  t: number
  level: 'error' | 'warn' | 'room'
  text: string
  /** A quién se refiere, cuando quien lo anota lo sabe de primera mano (las
   *  entradas y salidas de la sala). Los mensajes de consola no lo traen y su
   *  dueño hay que deducirlo (ver DebugPanel). */
  who?: { id: string; name: string; isLocal: boolean }
}

/**
 * Cuántas entradas se guardan. Una clase larga con un fallo repetitivo llena
 * esto en minutos; lo último es lo que se está investigando, así que el búfer
 * tira lo más viejo.
 */
const MAX_ENTRIES = 200

/** Un mensaje muy largo (un volcado de objeto) se recorta: lo que interesa es
 *  el principio, y 200 de estos tienen que caber en memoria sin pensarlo. */
const MAX_TEXT = 2000

const entries: DebugEntry[] = []

/**
 * Las entradas y salidas de la sala van en su PROPIO búfer, no en el de los
 * errores.
 *
 * Si compartieran uno, una clase de 250 personas entrando en dos minutos
 * expulsaría del registro exactamente lo que se ha ido a mirar. Y separadas
 * siguen sirviendo para lo que sirven: un «no se le oye» se entiende de otra
 * manera cuando justo encima pone que esa persona se fue de la sala hace diez
 * segundos.
 */
const roomEntries: DebugEntry[] = []
const MAX_ROOM_ENTRIES = 100

const startedAt = Date.now()
let installed = false

const stringify = (value: unknown): string => {
  if (typeof value === 'string') return value
  if (value instanceof Error) return `${value.name}: ${value.message}`
  try {
    return JSON.stringify(value)
  } catch {
    // Referencias circulares, elementos del DOM, objetos del SDK...
    return String(value)
  }
}

/**
 * Tapar lo que no debe salir de este navegador.
 *
 * Este registro se COPIA: existe para pegarlo en un chat o en un correo cuando
 * algo se tuerce en una clase. Y lo que escribe el SDK de LiveKit por consola
 * incluye la dirección del SFU y, pegado detrás, un token de entrada válido:
 *
 *   signal connecting to wss://<host>/rtc/v1?access_token=eyJhbGciOi…
 *
 * Ese token no es un identificador, es una credencial: sirve para entrar en esa
 * sala con el rol que lleve dentro, y dura horas. Así que del registro sale con
 * el valor sustituido, y la dirección del SFU también — quién aloja los medios
 * no ayuda a diagnosticar nada y es justo lo que no interesa repartir.
 *
 * Se conserva el ESQUEMA (`wss://…` frente a `https://…`) y el nombre del
 * parámetro, porque eso sí dice algo al leer el registro: distingue «no llegó a
 * pedir el token» de «lo pidió y le respondieron».
 */
const redact = (text: string): string =>
  text
    // Primero la dirección COMPLETA, con su cadena de consulta: se come de una
    // vez el host y cualquier credencial que fuera detrás, y deja una sola
    // marca en vez de una por regla.
    .replace(/\b(wss?|https?):\/\/[^\s"'<>)]+/gi, '$1://<oculto>')
    // Credenciales que no venían dentro de una dirección.
    .replace(/([?&](?:access_token|token|g|nonce|key|secret)=)[^&\s"']+/gi, '$1<oculto>')
    // JWT suelto, sin parámetro que lo introduzca.
    .replace(/\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '<jwt oculto>')

const push = (level: DebugEntry['level'], args: unknown[]): void => {
  const text = redact(args.map(stringify).join(' ')).slice(0, MAX_TEXT)
  entries.push({ t: Date.now() - startedAt, level, text })
  if (entries.length > MAX_ENTRIES) entries.splice(0, entries.length - MAX_ENTRIES)
}

/** Empezar a escuchar. Idempotente: llamarlo dos veces no duplica mensajes. */
export function installDebugLog(): void {
  if (installed) return
  installed = true

  const origError = console.error.bind(console)
  const origWarn = console.warn.bind(console)

  console.error = (...args: unknown[]) => { push('error', args); origError(...args) }
  console.warn = (...args: unknown[]) => { push('warn', args); origWarn(...args) }

  // Lo que nunca llega a console.error: una excepción que nadie capturó y una
  // promesa rechazada sin catch. Suelen ser justo las interesantes.
  window.addEventListener('error', (e) => {
    push('error', [`window.onerror: ${e.message}`, `${e.filename}:${e.lineno}`])
  })
  window.addEventListener('unhandledrejection', (e) => {
    push('error', ['unhandledrejection:', stringify((e as PromiseRejectionEvent).reason)])
  })
}

/**
 * Anotar que alguien entra o sale de la sala.
 *
 * Lo llama quien lo sabe con certeza (RoomPage, mirando la lista de la sala),
 * así que esta entrada SÍ viene con dueño y no hay que deducirlo de un texto.
 */
export function logRoomEvent(text: string, who?: DebugEntry['who']): void {
  roomEntries.push({ t: Date.now() - startedAt, level: 'room', text, who })
  if (roomEntries.length > MAX_ROOM_ENTRIES) {
    roomEntries.splice(0, roomEntries.length - MAX_ROOM_ENTRIES)
  }
}

/** Lo registrado hasta ahora, lo más reciente al final. */
export function debugEntries(): DebugEntry[] {
  return entries.slice()
}

/** Las entradas y salidas de la sala, lo más reciente al final. */
export function debugRoomEvents(): DebugEntry[] {
  return roomEntries.slice()
}

/** Cuánto lleva viva la página, en ms. */
export function debugUptimeMs(): number {
  return Date.now() - startedAt
}
